import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  List,
  ListItem,
  Typography,
  Tabs,
  Tab,
  Slide,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, setDoc, Timestamp, getDocs, getDoc } from 'firebase/firestore';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`order-tabpanel-${index}`}
      aria-labelledby={`order-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Slide direction="left" in={value === index} mountOnEnter unmountOnExit>
          <Box sx={{ p: 3 }}>{children}</Box>
        </Slide>
      )}
    </div>
  );
}

const ViewOrder = ({ unitCode, companyCode, unitType, companyName, isPureVeg, userId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location?.state;
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [assignedItems, setAssignedItems] = useState({});
  const itemRefs = useRef({});
  const highlightOrderId = state?.orderId;
  const highlightItemId = state?.itemId;

  const fetchAssignedItems = () => {
    if (!unitCode || !companyCode || !userId) {
      setError('Missing unit code, company code, or user ID. Please ensure you are logged in correctly.');
      console.error('ViewOrder: Missing required data', { unitCode, companyCode, userId, query: location?.search });
      return () => {};
    }

    try {
      console.log('ViewOrder: Fetching assigned menu items from companymenu', { unitCode, companyCode, userId });
      const menuQuery = query(
        collection(db, 'companymenu'),
        where('unitCode', '==', unitCode),
        where('companyCode', '==', companyCode),
        where('staff', '==', userId)
      );

      const unsubscribe = onSnapshot(menuQuery, (snapshot) => {
        try {
          const itemMap = {};
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            itemMap[doc.id] = { name: data.name, imageUrl: data.imageUrl || '', isVeg: data.isVeg || false };
            console.log('ViewOrder: Assigned menu item:', { itemId: doc.id, name: data.name, staff: data.staff, isVeg: data.isVeg, unitCode, companyCode });
          });
          console.log('ViewOrder: Assigned menu items:', { itemIds: Object.keys(itemMap), unitCode, companyCode, userId, documentCount: snapshot.size });
          if (Object.keys(itemMap).length === 0) {
            setError('No menu items assigned to you for this unit.');
          } else {
            setError(null);
          }
          setAssignedItems(itemMap);
        } catch (err) {
          console.error('ViewOrder: Error processing snapshot:', { err, unitCode, companyCode, userId });
          setError(`Failed to load assigned menu items: ${err.message}`);
          setAssignedItems({});
        }
      }, (err) => {
        console.error('ViewOrder: Snapshot listener error:', { err, unitCode, companyCode, userId });
        setError(`Failed to listen for menu item updates: ${err.message}`);
        setAssignedItems({});
      });

      return unsubscribe;
    } catch (err) {
      console.error('ViewOrder: Error setting up snapshot listener:', { err, unitCode, companyCode, userId });
      setError(`Failed to load assigned menu items: ${err.message}`);
      setAssignedItems({});
      return () => {};
    }
  };

  useEffect(() => {
    console.log('ViewOrder: Received props', { unitCode, companyCode, unitType, companyName, isPureVeg, userId });
    if (!unitCode || !companyCode || !userId) {
      setError('Missing unit code, company code, or user ID. Please ensure you are logged in correctly.');
      console.error('ViewOrder: Missing required data', { unitCode, companyCode, userId, query: location?.search });
      setItems([]);
      return;
    }

    sessionStorage.setItem('unitCode', unitCode);
    sessionStorage.setItem('companyCode', companyCode);
    sessionStorage.setItem('unitType', unitType || 'Unknown');
    sessionStorage.setItem('companyName', companyName || 'Unknown Company');
    sessionStorage.setItem('isPureVeg', isPureVeg ? 'true' : 'false');
    sessionStorage.setItem('userId', userId);

    const unsubscribeMenu = fetchAssignedItems();

    let unsubscribeOrders;
    if (Object.keys(assignedItems).length > 0) {
      const ordersQuery = query(
        collection(db, 'orders'),
        where('unitCode', '==', unitCode),
        where('companyCode', '==', companyCode)
      );

      unsubscribeOrders = onSnapshot(ordersQuery, (snapshot) => {
        try {
          console.log('ViewOrder: Received orders snapshot update, size:', snapshot.size, 'unitCode:', unitCode, 'companyCode:', companyCode);
          const fetchedItems = [];

          snapshot.docs.forEach(doc => {
            const orderData = doc.data();
            const assignedOrderItems = orderData.items.filter(item => assignedItems[item.id]);

            assignedOrderItems.forEach((item, itemIndex) => {
              if (!assignedItems[item.id]) {
                console.warn('ViewOrder: Item ID mismatch detected:', {
                  orderId: orderData.orderId,
                  itemId: item.id,
                  assignedItemId: assignedItems[item.id]?.id || 'none',
                  unitCode,
                  companyCode,
                });
              }
              fetchedItems.push({
                orderId: orderData.orderId,
                tableId: orderData.tableId || 'N/A',
                createdAt: orderData.createdAt,
                itemId: item.id,
                itemInstanceId: item.itemInstanceId,
                itemIndex: itemIndex,
                name: assignedItems[item.id]?.name || item.name || 'Unknown Item',
                quantity: item.quantity ?? 1,
                cookingInstructions: item.cookingInstructions || 'No instructions',
                status: item.status || 'Confirmed',
                imageUrl: assignedItems[item.id]?.imageUrl || '',
                isVeg: assignedItems[item.id]?.isVeg || false,
                orderDocId: doc.id,
              });
            });
          });

          const sortedItems = fetchedItems.sort((a, b) => {
            const aTime = a.createdAt instanceof Timestamp ? a.createdAt.toDate() : new Date(a.createdAt || 0);
            const bTime = b.createdAt instanceof Timestamp ? b.createdAt.toDate() : new Date(b.createdAt || 0);
            return aTime - bTime;
          });

          setItems(sortedItems);
          setError(sortedItems.length === 0 ? `No orders found for your assigned items in unit: ${unitCode}` : null);
          console.log('ViewOrder: Updated items:', sortedItems.map(i => ({
            orderId: i.orderId,
            itemInstanceId: i.itemInstanceId,
            itemId: i.itemId,
            name: i.name,
            status: i.status,
            unitCode,
            companyCode,
            isVeg: i.isVeg,
          })));
        } catch (err) {
          console.error('ViewOrder: Error processing orders snapshot:', { err, unitCode, companyCode, userId });
          setError(`Failed to load orders: ${err.message}`);
          setItems([]);
        }
      }, (err) => {
        console.error('ViewOrder: Orders snapshot listener error:', { err, unitCode, companyCode, userId });
        setError(`Failed to listen for order updates: ${err.message}`);
        setItems([]);
      });
    }

    return () => {
      if (unsubscribeMenu) unsubscribeMenu();
      if (unsubscribeOrders) unsubscribeOrders();
    };
  }, [unitCode, companyCode, userId, assignedItems]);

  useEffect(() => {
    if (highlightOrderId || highlightItemId) {
      console.log('ViewOrder: Highlight requested:', { highlightOrderId, highlightItemId, unitCode, companyCode });
      const item = items.find(i => 
        (highlightItemId && i.itemInstanceId === highlightItemId && i.orderId === highlightOrderId) ||
        (!highlightItemId && i.orderId === highlightOrderId)
      );
      if (item) {
        console.log('ViewOrder: Found item to highlight:', { orderId: item.orderId, itemInstanceId: item.itemInstanceId, unitCode, companyCode });
        const statusIndex = ['Confirmed', 'Preparing', 'Ready', 'All'].indexOf(item.status);
        if (statusIndex !== -1) {
          console.log('ViewOrder: Setting tabValue to:', statusIndex);
          setTabValue(statusIndex);
          setTimeout(() => {
            const itemRef = itemRefs.current[`${item.orderId}_${item.itemInstanceId}`];
            if (itemRef) {
              console.log('ViewOrder: Scrolling to item:', { orderId: item.orderId, itemInstanceId: item.itemInstanceId, unitCode, companyCode });
              itemRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
              console.log('ViewOrder: No ref found for item:', { orderId: item.orderId, itemInstanceId: item.itemInstanceId, unitCode, companyCode });
            }
          }, 500);
        }
      } else {
        console.log('ViewOrder: Item or order not found for highlight:', { highlightOrderId, highlightItemId, unitCode, companyCode });
      }
    }
  }, [highlightOrderId, highlightItemId, items, unitCode, companyCode]);

  const handleTabChange = (event, newValue) => {
    console.log('ViewOrder: Changing tab to:', newValue, 'unitCode:', unitCode, 'companyCode:', companyCode);
    setTabValue(newValue);
  };

  const handleItemStatusChange = async (orderDocId, itemInstanceId, newStatus) => {
    try {
      console.log('ViewOrder: Updating item status:', { orderDocId, itemInstanceId, newStatus, unitCode, companyCode });
      const orderDocRef = doc(db, 'orders', orderDocId);
      const orderDoc = await getDoc(orderDocRef);
      if (!orderDoc.exists()) {
        throw new Error('Order not found');
      }

      const orderData = orderDoc.data();
      if (orderData.unitCode !== unitCode || orderData.companyCode !== companyCode) {
        throw new Error(`Order ${orderDocId} does not belong to unit ${unitCode} or company ${companyCode}`);
      }
      const assignedItemIds = Object.keys(assignedItems);
      const targetItem = orderData.items.find(item => item.itemInstanceId === itemInstanceId);
      if (!targetItem) {
        throw new Error(`Item instance ${itemInstanceId} not found in order ${orderDocId}`);
      }
      if (!assignedItemIds.includes(targetItem.id)) {
        throw new Error(`Item ${targetItem.id} not assigned to user ${userId} in unit ${unitCode}`);
      }

      const updatedItems = orderData.items.map(item =>
        item.itemInstanceId === itemInstanceId ? { ...item, status: newStatus } : item
      );

      let orderStatus;
      if (updatedItems.every(item => item.status === 'Served')) {
        orderStatus = 'Served';
      } else if (updatedItems.some(item => item.status === 'Confirmed')) {
        orderStatus = 'Confirmed';
      } else if (updatedItems.some(item => item.status === 'Preparing')) {
        orderStatus = 'Preparing';
      } else if (updatedItems.every(item => item.status === 'Ready')) {
        orderStatus = 'Ready';
      } else {
        orderStatus = orderData.status;
      }

      const updates = {
        items: updatedItems,
        status: orderStatus,
      };

      console.log('ViewOrder: Applying Firestore updates:', { updates, unitCode, companyCode });
      await updateDoc(orderDocRef, updates);
      console.log('ViewOrder: Firestore update successful for orderDocId:', orderDocId, 'unitCode:', unitCode, 'companyCode:', companyCode);

      const cartQuery = query(
      collection(db, "carts"),
      where("orderId", "==", orderData.orderId),
      where("unitCode", "==", orderData.unitCode),
      where("companyCode", "==", orderData.companyCode)
    );

    const cartSnapshot = await getDocs(cartQuery);

    /*const updatePromises = cartSnapshot.docs.map(async (cartDoc) => {
      const cartData = cartDoc.data();
      const updatedCartItems = cartData.items.map(item =>
        item.id === targetItem.id  // 🔑 match by master id
          ? { ...item, status: newStatus }
          : item
      );

      return updateDoc(doc(db, "carts", cartDoc.id), {
        items: updatedCartItems
      });
    });*/
    const updatePromises = cartSnapshot.docs.map(async (cartDoc) => {
  const cartData = cartDoc.data();

  const updatedCartItems = cartData.items.map(item =>
    item.id === targetItem.id
      ? { ...item, status: newStatus }
      : item
  );

  // 🔥 Compute cartStatus (same logic as orders)
  let cartStatus;
  if (updatedCartItems.every(item => item.status === "Served")) {
    cartStatus = "Served";
  } else if (updatedCartItems.some(item => item.status === "Confirmed")) {
    cartStatus = "Confirmed";
  } else if (updatedCartItems.some(item => item.status === "Preparing")) {
    cartStatus = "Preparing";
  } else if (updatedCartItems.every(item => item.status === "Ready")) {
    cartStatus = "Ready";
  } else {
    cartStatus = cartData.cartStatus; // fallback
  }

  return updateDoc(doc(db, "carts", cartDoc.id), {
    items: updatedCartItems,
    cartStatus: cartStatus, // ✅ important
  });
});

    await Promise.all(updatePromises);
    console.log("✅ Cart items updated successfully");

      if (newStatus === 'Ready') {
        const itemName = assignedItems[targetItem.id]?.name || targetItem.name || 'Unknown Item';
        const notificationId = `server_${orderData.orderId}_${itemInstanceId}_${Date.now()}`;
        const notificationData = {
          orderId: orderData.orderId,
          itemId: targetItem.id,
          itemInstanceId: itemInstanceId,
          tableId: orderData.tableId || 'N/A',
          unitCode: orderData.unitCode || unitCode,
          companyCode: orderData.companyCode || companyCode,
          message: `Item ${itemName} in Order ${orderData.orderId} for ${unitType} ${orderData.tableId || 'N/A'} is ready for delivery`,
          role: 'servers',
          createdAt: Timestamp.fromDate(new Date()),
          read: false,
        };
        console.log('ViewOrder: Creating notification:', { notificationData, unitCode, companyCode });
        await setDoc(doc(db, 'notifications', notificationId), notificationData);
        console.log('ViewOrder: Notification sent to server staff:', { notificationId, unitCode, companyCode });
      }
    } catch (err) {
      console.error('ViewOrder: Error in handleItemStatusChange:', { err, unitCode, companyCode, orderDocId, itemInstanceId });
      setError(`Failed to update item status or send notification: ${err.message}`);
    }
  };

  const handleBack = () => {
    console.log('ViewOrder: Navigating to /kitchen_staff/home', { unitCode, companyCode, unitType, companyName, isPureVeg, userId });
    sessionStorage.setItem('unitCode', unitCode);
    sessionStorage.setItem('companyCode', companyCode);
    sessionStorage.setItem('unitType', unitType || 'Unknown');
    sessionStorage.setItem('companyName', companyName || 'Unknown Company');
    sessionStorage.setItem('isPureVeg', isPureVeg ? 'true' : 'false');
    sessionStorage.setItem('userId', userId);
    navigate(`/kitchen_staff/home?unit=${unitCode}&code=${companyCode}&type=${unitType || 'Unknown'}&name=${encodeURIComponent(companyName || 'Unknown Company')}&isPureVeg=${isPureVeg || false}`, {
      state: { unitCode, companyCode, unitType, companyName, isPureVeg, userId },
    });
  };

  const statusGroups = {
    Confirmed: items.filter(item => item.status === 'Confirmed'),
    Preparing: items.filter(item => item.status === 'Preparing'),
    Ready: items.filter(item => item.status === 'Ready'),
    All: items,
  };

  const renderItemList = (status, itemList) => (
    <Box>
      {itemList.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No {status.toLowerCase()} items for your assigned items in {unitType} unit.
        </Typography>
      ) : (
        <List>
          {itemList.map((item, index) => (
            <React.Fragment key={`${item.orderId}_${item.itemInstanceId}`}>
              <ListItem
                sx={{
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  py: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  mb: 2,
                }}
                ref={el => (itemRefs.current[`${item.orderId}_${item.itemInstanceId}`] = el)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    style={{ width: 60, height: 60, marginRight: 16, objectFit: 'cover', borderRadius: '4px' }}
                    onError={(e) => { e.target.src = '/placeholder.png'; }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {item.name} {item.isVeg && isPureVeg ? '(Veg)' : ''}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                      {item.cookingInstructions}
                    </Typography>
                    <Typography variant="body2">
                      Quantity: {item.quantity}
                    </Typography>
                    <Typography variant="body2">
                      Order ID: {item.orderId}
                    </Typography>
                    <Typography variant="body2">
                      Table ID: {item.tableId}
                    </Typography>
                    <Typography variant="body2">
                      Created At: {item.createdAt instanceof Timestamp ? item.createdAt.toDate().toLocaleString() : item.createdAt || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      Status: {item.status}
                    </Typography>
                  </Box>
                  {status !== 'All' && status !== 'Ready' && item.status !== 'Served' && (
                    <Select
                      value={item.status}
                      onChange={(e) => handleItemStatusChange(item.orderDocId, item.itemInstanceId, e.target.value)}
                      size="small"
                      sx={{ minWidth: 120 }}
                    >
                      {['Confirmed', 'Preparing', 'Ready'].map(statusOption => (
                        <MenuItem key={statusOption} value={statusOption}>{statusOption}</MenuItem>
                      ))}
                    </Select>
                  )}
                </Box>
              </ListItem>
              {index < itemList.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );

  /*const renderItemList = (status, itemList) => (
  <Box>
    {itemList.length === 0 ? (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ py: 2, textAlign: "center" }}
      >
        No {status.toLowerCase()} items for your assigned items in {unitType} unit.
      </Typography>
    ) : (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Image</strong></TableCell>
              <TableCell><strong>Item</strong></TableCell>
              <TableCell><strong>Qty</strong></TableCell>
              <TableCell><strong>Order ID</strong></TableCell>
              <TableCell><strong>Table</strong></TableCell>
              <TableCell><strong>Instructions</strong></TableCell>
              <TableCell><strong>Created At</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              {status !== "All" && status !== "Ready" && (
                <TableCell><strong>Action</strong></TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {itemList.map((item) => (
              <TableRow
                key={`${item.orderId}_${item.itemInstanceId}`}
                ref={(el) =>
                  (itemRefs.current[
                    `${item.orderId}_${item.itemInstanceId}`
                  ] = el)
                }
              >
                <TableCell>
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    style={{
                      width: 50,
                      height: 50,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                    onError={(e) => {
                      e.target.src = "/placeholder.png";
                    }}
                  />
                </TableCell>

                <TableCell>
                  {item.name} {item.isVeg && isPureVeg ? "(Veg)" : ""}
                </TableCell>

                <TableCell>{item.quantity}</TableCell>

                <TableCell>{item.orderId}</TableCell>

                <TableCell>{item.tableId}</TableCell>

                <TableCell>{item.cookingInstructions}</TableCell>

                <TableCell>
                  {item.createdAt instanceof Timestamp
                    ? item.createdAt.toDate().toLocaleString()
                    : item.createdAt || "N/A"}
                </TableCell>

                <TableCell>{item.status}</TableCell>

                {status !== "All" && status !== "Ready" && item.status !== "Served" && (
                  <TableCell>
                    <Select
                      value={item.status}
                      onChange={(e) =>
                        handleItemStatusChange(
                          item.orderDocId,
                          item.itemInstanceId,
                          e.target.value
                        )
                      }
                      size="small"
                      sx={{ minWidth: 120 }}
                    >
                      {["Confirmed", "Preparing", "Ready"].map(
                        (statusOption) => (
                          <MenuItem key={statusOption} value={statusOption}>
                            {statusOption}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);*/

  const statuses = ['Confirmed', 'Preparing', 'Ready', 'All'];

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {unitType} Items - {companyName || 'Unknown Company'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          centered
          aria-label="item status tabs"
          sx={{ 
            '& .MuiTab-root': { 
              fontWeight: 'medium', 
              textTransform: 'none',
              mx: 2,
            } 
          }}
        >
          {statuses.map((status, index) => (
            <Tab
              key={status}
              label={status}
              id={`order-tab-${index}`}
              aria-controls={`order-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {items.length > 0 ? (
        statuses.map((status, index) => (
          <TabPanel key={status} value={tabValue} index={index}>
            {renderItemList(status, statusGroups[status])}
          </TabPanel>
        ))
      ) : !error ? (
        <Typography variant="body1" sx={{ textAlign: 'center', py: 2 }}>
          Loading items for {unitType} unit...
        </Typography>
      ) : null}

      <Button variant="contained" color="primary" onClick={handleBack} sx={{ mt: 3 }}>
        Back to Home
      </Button>
    </Container>
  );
};

export default ViewOrder;